import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { FileText, Plus, Pencil, Trash2, RefreshCw, DollarSign, Search } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface Product {
  id: number;
  name: string;
  price: string;
  priceWithTax?: string;
  sku: string;
}

interface ProcedureType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  productId: number | null;
  responseTimeDays: number | null;
  targetAudience: 'alumno' | 'profesor' | 'ambos';
  product: Product | null;
  isActive: boolean;
  hasCost: boolean;
  createdAt: string;
}

interface FormData {
  code: string;
  name: string;
  description: string;
  productId: number | null;
  responseTimeDays: string;
  targetAudience: 'alumno' | 'profesor' | 'ambos';
}

const AUDIENCE_LABELS: Record<string, { label: string; color: string }> = {
  alumno: { label: 'Alumnos', color: 'bg-blue-100 text-blue-800' },
  profesor: { label: 'Profesores', color: 'bg-purple-100 text-purple-800' },
  ambos: { label: 'Ambos', color: 'bg-green-100 text-green-800' },
};

export default function ProcedureTypesList() {
  const { toast } = useToast();
  const [types, setTypes] = useState<ProcedureType[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    description: '',
    productId: null,
    responseTimeDays: '',
    targetAudience: 'alumno',
  });

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/procedures/types', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTypes(data);
      }
    } catch (error) {
      console.error('Error fetching procedure types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/sales/products', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.filter((p: any) => p.isActive));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchTypes();
    fetchProducts();
  }, []);

  const handleOpenDialog = (type?: ProcedureType) => {
    if (type) {
      setEditingId(type.id);
      setFormData({
        code: type.code,
        name: type.name,
        description: type.description || '',
        productId: type.productId,
        responseTimeDays: type.responseTimeDays?.toString() || '',
        targetAudience: type.targetAudience || 'alumno',
      });
    } else {
      setEditingId(null);
      setFormData({ code: '', name: '', description: '', productId: null, responseTimeDays: '', targetAudience: 'alumno' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.code.trim() || !formData.name.trim()) {
      toast({ title: 'Error', description: 'Clave y nombre son requeridos', variant: 'destructive' });
      return;
    }

    try {
      const url = editingId ? `/api/procedures/types/${editingId}` : '/api/procedures/types';
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: formData.code.trim(),
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          productId: formData.productId,
          responseTimeDays: formData.responseTimeDays ? parseInt(formData.responseTimeDays) : null,
          targetAudience: formData.targetAudience,
        }),
      });

      if (response.ok) {
        toast({ title: 'Guardado', description: editingId ? 'Tipo de trámite actualizado' : 'Tipo de trámite creado' });
        setShowDialog(false);
        fetchTypes();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Error al guardar', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      const response = await fetch(`/api/procedures/types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !isActive }),
      });

      if (response.ok) {
        toast({ title: 'Actualizado', description: `Tipo de trámite ${!isActive ? 'activado' : 'desactivado'}` });
        fetchTypes();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error al actualizar', variant: 'destructive' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar este tipo de trámite?')) return;

    try {
      const response = await fetch(`/api/procedures/types/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({ title: 'Eliminado', description: 'Tipo de trámite eliminado' });
        fetchTypes();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Error al eliminar', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  const filteredTypes = types
    .filter(t =>
      t.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.code.localeCompare(b.code, 'es', { sensitivity: 'base' }));

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parseFloat(amount));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Trámites Académicos</h1>
            <p className="text-muted-foreground">Catálogo de tipos de trámite</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchTypes} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Tipo
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por clave o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
          ) : filteredTypes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No se encontraron resultados' : 'No hay tipos de trámite registrados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Clave</th>
                    <th className="text-left py-3 px-4 font-medium">Trámite</th>
                    <th className="text-left py-3 px-4 font-medium">Descripción</th>
                    <th className="text-center py-3 px-4 font-medium">Dirigido a</th>
                    <th className="text-left py-3 px-4 font-medium">Producto de Cobro</th>
                    <th className="text-center py-3 px-4 font-medium">Tiempo de Respuesta</th>
                    <th className="text-center py-3 px-4 font-medium">Activo</th>
                    <th className="text-center py-3 px-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTypes.map((type) => (
                    <tr key={type.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4 font-mono text-sm">{type.code}</td>
                      <td className="py-3 px-4 font-medium">{type.name}</td>
                      <td className="py-3 px-4 text-muted-foreground text-sm max-w-xs truncate">
                        {type.description || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${AUDIENCE_LABELS[type.targetAudience]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {AUDIENCE_LABELS[type.targetAudience]?.label || type.targetAudience}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {type.hasCost && type.product ? (
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-green-600" />
                            <span className="text-sm">
                              {type.product.name} - {formatCurrency(type.product.priceWithTax ?? type.product.price)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Sin costo</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-sm">
                        {type.responseTimeDays
                          ? `${type.responseTimeDays} día${type.responseTimeDays > 1 ? 's' : ''}`
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Switch
                          checked={type.isActive}
                          onCheckedChange={() => handleToggleActive(type.id, type.isActive)}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleOpenDialog(type)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(type.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Tipo de Trámite' : 'Nuevo Tipo de Trámite'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="code">Clave *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="Ej: CONST_EST"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nombre del Trámite *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Constancia de Estudios"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción del trámite..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Dirigido a</Label>
              <Select
                value={formData.targetAudience}
                onValueChange={(val) => setFormData({ ...formData, targetAudience: val as 'alumno' | 'profesor' | 'ambos' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar audiencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alumno">Alumnos</SelectItem>
                  <SelectItem value="profesor">Profesores</SelectItem>
                  <SelectItem value="ambos">Ambos</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Indica si este trámite es para alumnos, profesores o ambos
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="responseTimeDays">Tiempo de Respuesta (días)</Label>
              <Input
                id="responseTimeDays"
                type="number"
                min="1"
                value={formData.responseTimeDays}
                onChange={(e) => setFormData({ ...formData, responseTimeDays: e.target.value })}
                placeholder="Ej: 5"
              />
              <p className="text-xs text-muted-foreground">
                Días hábiles estimados para resolver el trámite (KPI)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="productId">Producto de Cobro</Label>
              <Select
                value={formData.productId?.toString() || 'none'}
                onValueChange={(val) => setFormData({ ...formData, productId: val === 'none' ? null : parseInt(val) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin Costo</SelectItem>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id.toString()}>
                      {product.name} - {formatCurrency(product.price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Si el trámite no tiene costo, seleccione "Sin Costo"
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
